/**
 * `spec_import` — the headline of phase 5, and the branch that reads nothing.
 *
 * ## Why import beats tool-by-tool creation, by a wide margin
 *
 * The owner's ask was *"give you our backend and say create all endpoints
 * inside Apiboo Collections"*. One `spec_import` replaces N `request_create`
 * calls; the endpoint shapes come from the project's OWN spec rather than from
 * a model's recollection of it; and every byte written came from a file the
 * agent already had. `request_create` stays because a project without a spec is
 * the common case, but this is the tool that answers the question.
 *
 * ## `content`, NEVER a `url`
 *
 * Fetching a url would mean an arbitrary-HTTP primitive, and
 * `send_http_request` is pinned as unreachable from every MCP module
 * (`mcpAttackSurface.test.js`, 20a2). It is also unnecessary: the agent is
 * reading the user's repository, so it has the file. The tool's schema has no
 * `url` property, the description says so, and a Rust test asserts both — but
 * the load-bearing fact is that there is no code path here that could fetch
 * one.
 *
 * ## What the parser does and does NOT produce — say this to the user
 *
 * `parseImportFile` is the app's own pure parser (`utils/collectionImport/
 * index.js:51`), the same one the Import dialog uses. Two of its behaviours
 * matter enough to disclose:
 *
 *   * **Postman v2 and Insomnia bring their folder tree** — `parsePostman.js`
 *     walks nested `item` groups into `settings.folders` with `folder_id` on
 *     each request. So do the Bruno collection exports (JSON, bundled
 *     OpenCollection YAML) and Hoppscotch collection JSON (27.09.2026).
 *   * **A Bruno collection FOLDER cannot come through here.** This tool takes
 *     one text; a folder is many files and is imported with the app's Import
 *     dialog (`parseImportFolder`). Say so rather than trying file by file.
 *   * **Environments are never written** (O37). A Bruno export can carry
 *     environments; they are dropped with a warning naming how many.
 *   * **OpenAPI and Swagger group by TAG** (since 13.09.2026). The parser
 *     itself — not this tool — puts each operation in the folder of its first
 *     tag, so the Import dialog and this tool still agree about the same file.
 *     Untagged operations, and every operation of a spec with no tags, stay
 *     at the collection root. HAR has no grouping at all.
 *
 * ## `dry_run`
 *
 * Parse, report, write nothing. Worth having because the failure mode this
 * tool actually has is a spec that parses into something the agent did not
 * expect, and finding that out after 200 requests have landed in the user's
 * sidebar is much worse than finding it out before.
 */
import { parseImportFile } from '../../utils/collectionImport';
import { mapImportedRequestToLocal } from '../../utils/requestMappers';
import { generateLocalEntityId } from '../../utils/localIds';
import { mcpError } from '../errors';
import { applyCollectionsChange, recordBytes, OUTBOX_ITEM_MAX_BYTES } from '../collectionsDb';

/** `services/ai/importSpec.js`'s cap, matched deliberately. */
export const MAX_SPEC_BYTES = 5 * 1024 * 1024;

/**
 * `parseImportFile` takes a FILENAME, and uses it only to pick between JSON,
 * YAML and the two plain-text formats (`index.js:11-38`) and as a fallback
 * collection name. A client sends `format_hint`, not a filename — its own
 * filename is not a property of the user's workspace and we have no use for
 * it.
 */
const HINT_FILENAMES = Object.freeze({
  json: 'spec.json',
  yaml: 'spec.yaml',
  har: 'spec.har',
  bru: 'spec.bru',
});

export async function specImport(args = {}) {
  if (typeof args.content !== 'string' || !args.content.trim()) {
    return mcpError(
      'invalid_argument',
      '`content` is required: pass the specification file\'s TEXT. This tool does not fetch a url — you are reading the user\'s repository, so read the file and pass what it says.'
    );
  }
  // A url passed as `content` is the mistake this tool exists to refuse
  // loudly. Left to the parser it would come back as "Unrecognized format",
  // which teaches a model nothing about WHY.
  if (/^\s*https?:\/\/\S+\s*$/i.test(args.content)) {
    return mcpError(
      'invalid_argument',
      'That is a url, not a specification. This tool never fetches anything — read the file yourself and pass its text as `content`.'
    );
  }
  const bytes = new TextEncoder().encode(args.content).length;
  if (bytes > MAX_SPEC_BYTES) {
    return mcpError(
      'invalid_argument',
      `That specification is ${bytes} bytes; the limit is ${MAX_SPEC_BYTES}. Nothing was written. Split it, or import the part the user asked about.`,
      { bytes, cap: MAX_SPEC_BYTES }
    );
  }
  if (args.format_hint != null && !HINT_FILENAMES[String(args.format_hint)]) {
    return mcpError(
      'invalid_argument',
      `\`format_hint\` must be one of ${Object.keys(HINT_FILENAMES).join(', ')}.`
    );
  }
  const nameOverride =
    typeof args.collection_name === 'string' ? args.collection_name.trim() : '';
  if (nameOverride.length > 200) {
    return mcpError('invalid_argument', '`collection_name` is longer than 200 characters.');
  }

  let parsed;
  try {
    parsed = parseImportFile(args.content, HINT_FILENAMES[String(args.format_hint)] || '');
  } catch (err) {
    // The parser's own messages are written FOR A USER — "Unrecognized
    // format. Supported: …", "Invalid JSON" — and name no path, no id and no
    // content. They are the one place a relayed message is better than a fixed
    // one, because the correction depends on which format failed. Capped, so a
    // parser that ever starts echoing input cannot turn this into a channel.
    const detail = String(err?.message || '').slice(0, 300);
    return mcpError(
      'invalid_argument',
      `That specification could not be parsed. Nothing was written. ${detail}`
    );
  }

  if (parsed.kind !== 'collection') {
    // An ENVIRONMENT file (Postman or Hoppscotch). Importing it would mean
    // writing variable values, which is out of this phase entirely (**O37**)
    // — and it is exactly the file most likely to carry a real token.
    return mcpError(
      'invalid_argument',
      'That is an environment file, not a collection. This server does not import environments: an environment file carries variable VALUES, which it will not write. Nothing was written.'
    );
  }

  // O37 again: environments that came WITH a collection (Bruno exports) are
  // dropped here, never written, and the agent is told so.
  const envCount = (parsed.environments || []).length;
  const warnings = [...(parsed.warnings || [])];
  if (envCount) {
    warnings.push(
      `${envCount} environment${envCount === 1 ? ' was' : 's were'} in the file and ${envCount === 1 ? 'was' : 'were'} NOT imported — this server never writes variable values. The user can import ${envCount === 1 ? 'it' : 'them'} with Import in the app.`
    );
  }

  const incoming = parsed.collections || [];
  const plan = incoming.map((col) => ({
    name: nameOverride && incoming.length === 1 ? nameOverride : String(col.name || ''),
    description: typeof col.description === 'string' ? col.description : '',
    folderCount: (col.settings?.folders || []).length,
    requestCount: (col.requests || []).length,
  }));

  if (args.dry_run === true) {
    return {
      dryRun: true,
      format: parsed.format,
      formatLabel: parsed.formatLabel,
      warnings,
      wouldCreate: plan,
      scopesConsulted: [],
      scopesUnavailable: [],
    };
  }

  return applyCollectionsChange((collections) => {
    const built = [];
    const oversize = [];
    for (const col of incoming) {
      // A fresh prefix per collection: `mapImportedRequestToLocal` derives
      // request ids as `<prefix>-req-<index>`, so the prefix is what makes them
      // unique across two imports of the same file.
      const prefix = generateLocalEntityId('imp');
      const requests = (col.requests || []).map((r, i) => mapImportedRequestToLocal(r, prefix, i));
      for (const request of requests) {
        if (recordBytes(request) > OUTBOX_ITEM_MAX_BYTES) oversize.push(request.name);
      }
      built.push({
        id: prefix,
        name: nameOverride && incoming.length === 1 ? nameOverride : String(col.name || 'Imported Collection'),
        description: typeof col.description === 'string' ? col.description : '',
        settings: col.settings || {},
        requests,
      });
    }

    if (oversize.length) {
      // All-or-nothing: a half-imported spec is worse than a refused one,
      // because the agent has no way to tell which half landed.
      return {
        error: mcpError(
          'record_too_large',
          `${oversize.length} request(s) in that specification are individually too large to sync to the user's other devices, so NOTHING was imported.`,
          { cap: OUTBOX_ITEM_MAX_BYTES, requests: oversize.slice(0, 10) }
        ),
      };
    }

    return {
      next: [...collections, ...built],
      sizedRecords: built.map((c) => ({ ...c, requests: [] })),
      result: {
        created: 'import',
        format: parsed.format,
        formatLabel: parsed.formatLabel,
        warnings,
        collections: built.map((c) => ({
          collectionId: c.id,
          name: c.name,
          description: c.description,
          folderCount: (c.settings?.folders || []).length,
          requestCount: c.requests.length,
        })),
        // The disclosure the header argues for. A model that does not know an
        // OpenAPI import is flat will tell the user it organised something it
        // did not.
        note:
          'Postman, Insomnia, Bruno collection exports and Hoppscotch collections keep their own folders. OpenAPI and Swagger operations are grouped into one folder per tag (first tag wins); untagged operations, and HAR imports, sit at the collection root. A Bruno collection FOLDER cannot be imported through this tool — ask the user to export it from Bruno as one file, or to use Import in the app. A collection in the sidebar may be collapsed — tell the user its name.',
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}
